import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from "react";
import { styled } from "@mui/material/styles";
import { FormControl, Switch, InputLabel, Box } from "@mui/material";
import { Select, MenuItem } from "@mui/material";
import { RangePicker } from "react-minimal-datetime-range";
import "../../vendor/react-minimal-datetime-range.css";
import { ReactComponent as ArrowdownIcon } from "../../vendor/_new/icon/sites/arrow_down.svg";
import { getPeriodString } from "../pages/application/analytics/common";
import { normalizePickerDateRange } from "../pages/_new/detection/wafEventListUtils";
import colors from "../../utils/colors";
import { layerdRangePickerWrapperSx } from "./layerdDateTimePicker/styles";
import {
  EVENT_LISTING_PRESET_PERIODS,
  EVENT_LISTING_MAX_CUSTOM_DAYS,
  getDefaultEventListingCustomDateRange,
} from "../pages/_new/detection/eventTimeRange";

export { GreenTextField } from "./greenTextField";
export { SafeTextField, NameSafeTextField } from "./SafeTextField";

/* Styled FormControl that mimics GreenTextField */
export const GreenSelectForm = styled(FormControl)(({ theme, error }) => ({
  // Allow using <GreenSelectForm fullWidth> like your TextField
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    borderColor: error ? colors.red[500] : colors.neutral[300],
    // base border color on the fieldset
    "& fieldset": {
      borderColor: error ? colors.red[500] : colors.neutral[300],
    },
    // the visible select area (text padding)
    "& .MuiSelect-select": {
      paddingLeft: "16px",
      paddingRight: "16px",
      paddingBottom: "6px",
      paddingTop: "22px",
      fontSize: 14,
      lineHeight: "14px",
      color: colors.neutral[1000],
    },
  },

  // Focused state: indigo to match v2 theme
  "& .MuiOutlinedInput-root.Mui-focused": {
    borderColor: error ? colors.red[500] : colors.primary[300],
    "& fieldset": {
      borderColor: error ? colors.red[500] : colors.primary[300],
    },
  },

  // Label styles (normal + focused + shrink)
  "& .MuiInputLabel-root": {
    fontSize: 14,
    lineHeight: "14px",
    color: colors.neutral[800],
    paddingBottom: "4px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: colors.neutral[800],
  },
  "& .MuiInputLabel-shrink": {
    backgroundColor: "transparent!important",
    marginTop: "14px",
    color: colors.neutral[800],
    paddingTop: "0px!important",
    paddingLeft: "11px!important",
  },
  "& .MuiSelect-icon": {
    marginTop: "-4px",
  },
}));

/** v2 indigo toggle — overrides global MuiSwitch colorPrimary (yellow). */
export const GREEN_SWITCH_WIDTH = 36;
export const GREEN_SWITCH_HEIGHT = 19;

export const GreenSwitch = styled(Switch)({
  width: GREEN_SWITCH_WIDTH,
  height: GREEN_SWITCH_HEIGHT,
  padding: 0,
  margin: 0,
  flexShrink: 0,
  "& .MuiSwitch-switchBase": {
    padding: 2,
    transitionDuration: "160ms",
    "&.Mui-checked": {
      transform: "translateX(17px)",
      color: colors.neutral[100],
      "& + .MuiSwitch-track": {
        backgroundColor: colors.primary[300],
        opacity: 1,
        borderColor: colors.primary[300],
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 15,
    height: 15,
  },
  "& .MuiSwitch-track": {
    borderRadius: 10,
    opacity: 1,
    backgroundColor: colors.neutral[300],
    border: `1px solid ${colors.neutral[300]}`,
    boxSizing: "border-box",
  },
  "& .MuiSwitch-switchBase.Mui-disabled": {
    "& + .MuiSwitch-track": {
      backgroundColor: colors.neutral[200],
      borderColor: colors.neutral[200],
      opacity: 1,
    },
  },
  "& .MuiSwitch-switchBase.Mui-disabled.Mui-checked": {
    "& + .MuiSwitch-track": {
      backgroundColor: colors.neutral[300],
      borderColor: colors.neutral[300],
      opacity: 1,
    },
  },
});

const timeRangeMenuItemSx = {
  height: 38,
  fontSize: 14,
  lineHeight: "21px",
  color: colors.neutral[800],
  "&:hover": {
    backgroundColor: colors.primary.alpha,
    color: colors.primary[300],
  },
  "&.Mui-selected": {
    backgroundColor: colors.primary.alpha,
    color: colors.primary[500],
    fontWeight: 500,
  },
};

const timeRangeMenuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      borderRadius: "8px",
      border: `1px solid ${colors.neutral[200]}`,
      boxShadow: "0 4px 16px rgba(17, 17, 17, 0.08)",
      backgroundColor: colors.neutral[100],
    },
  },
  MenuListProps: { sx: { py: 0.5 } },
};

/** Styles aligned with GreenTextField + WAF react-minimal-datetime-range primary (#6371F7). */
const greenDateTimeLocalSx = {
  "& .MuiInputBase-input": {
    cursor: "pointer",
    fontSize: 14,
    lineHeight: "14px",
    color: colors.neutral[1000],
  },
  "& input[type='datetime-local']": {
    accentColor: colors.primary[300],
  },
  "& input::-webkit-calendar-picker-indicator": {
    cursor: "pointer",
    opacity: 0.72,
  },
};

/** Keep native datetime-local picker; block keyboard/paste entry only. */
export const pickerOnlyDateTimeLocalProps = {
  onKeyDown: (e) => {
    if (e.key === "Tab") return;
    e.preventDefault();
  },
  onPaste: (e) => e.preventDefault(),
  onClick: (e) => e.currentTarget.querySelector("input")?.showPicker?.(),
  sx: greenDateTimeLocalSx,
};

/** datetime-local field — Layerd single-datetime picker (same library UX as GreenTimeRangePicker). */
export { default as GreenDateTimeLocalField } from "./layerdDateTimePicker";

function isUsableCustomDateRange(customDateRange) {
  if (!Array.isArray(customDateRange) || customDateRange.length < 2) return false;
  const year = Number.parseInt(String(customDateRange[0]).slice(0, 4), 10);
  return Number.isFinite(year) && year >= 2010;
}

function splitCustomDateRange(customDateRange) {
  const [fallbackStart, fallbackEnd] = getDefaultEventListingCustomDateRange();
  const fallback = {
    dates: [fallbackStart.split("T")[0], fallbackEnd.split("T")[0]],
    times: [
      (fallbackStart.split("T")[1] || "00:00:00").substring(0, 5),
      (fallbackEnd.split("T")[1] || "23:59:59").substring(0, 5),
    ],
    key: "default",
  };
  if (!isUsableCustomDateRange(customDateRange)) return fallback;

  const dates = [
    customDateRange[0].split("T")[0],
    customDateRange[1].split("T")[0],
  ];
  const times = [
    (customDateRange[0].split("T")[1] || "00:00:00").substring(0, 5),
    (customDateRange[1].split("T")[1] || "00:00:00").substring(0, 5),
  ];
  return {
    dates,
    times,
    key: `${customDateRange[0]}|${customDateRange[1]}`,
  };
}

const DEFAULT_PRESET_PERIODS = ["30m", "60m", "3h", "6h", "24h", "3d", "7d"];
const RANGE_PICKER_ERROR_CLASS = "layerd-range-picker__range-error";

function validateCustomRangeSpan(normalized, maxCustomDays) {
  if (!maxCustomDays || !Array.isArray(normalized) || normalized.length < 2) return null;
  const start = new Date(normalized[0]);
  const end = new Date(normalized[1]);
  const spanMs = end.getTime() - start.getTime();
  const maxMs = maxCustomDays * 24 * 60 * 60 * 1000;
  if (!Number.isFinite(spanMs) || spanMs <= 0 || spanMs > maxMs) {
    return { start, end, spanMs };
  }
  return null;
}

function paintPickerRangeError(root, errorMessage) {
  const panel = root?.querySelector(".react-minimal-datetime-range");
  if (!panel) return false;

  let el = panel.querySelector(`.${RANGE_PICKER_ERROR_CLASS}`);
  if (!el) {
    el = document.createElement("div");
    el.className = RANGE_PICKER_ERROR_CLASS;
    panel.appendChild(el);
  }
  if (errorMessage) {
    el.textContent = errorMessage;
    el.style.display = "block";
  } else {
    el.textContent = "";
    el.style.display = "none";
  }
  return true;
}

function reopenPickerInstant(root, errorMessage) {
  if (!root) return;

  root.classList.add("layerd-range-picker--instant");
  const trigger = root.querySelector(".react-minimal-datetime-range__range-input-wrapper");
  trigger?.click();

  requestAnimationFrame(() => {
    const panel = root.querySelector(".react-minimal-datetime-range");
    if (panel) {
      panel.classList.add("visible");
      panel.style.transition = "none";
      panel.style.opacity = "1";
      panel.style.transform = "translateY(0) perspective(600px) rotateX(0)";
    }
    paintPickerRangeError(root, errorMessage);

    requestAnimationFrame(() => {
      root.classList.remove("layerd-range-picker--instant");
      if (panel) {
        panel.style.transition = "";
        panel.style.opacity = "";
        panel.style.transform = "";
      }
    });
  });
}

function usePickerRangeError(wrapperRef, errorMessage, pickerOpen) {
  useLayoutEffect(() => {
    if (!pickerOpen || !errorMessage) return undefined;
    paintPickerRangeError(wrapperRef.current, errorMessage);
    return undefined;
  }, [errorMessage, pickerOpen, wrapperRef]);
}

function useClearRangeErrorOnCalendarClick(wrapperRef, rangeError, setRangeError) {
  useEffect(() => {
    const root = wrapperRef.current;
    if (!root || !rangeError) return undefined;

    const onCalendarClick = (event) => {
      if (event.target.closest(".react-minimal-datetime-range-calendar")) {
        setRangeError("");
        paintPickerRangeError(root, "");
      }
    };

    root.addEventListener("click", onCalendarClick);
    return () => root.removeEventListener("click", onCalendarClick);
  }, [wrapperRef, rangeError, setRangeError]);
}

function usePickerOpenState(wrapperRef) {
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return undefined;

    const sync = () => {
      setPickerOpen(!!root.querySelector(".react-minimal-datetime-range.visible"));
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, [wrapperRef]);

  return pickerOpen;
}

export const GreenTimeRangePicker = ({
  selectPeriod,
  selectCustomDateRange,
  timeRange,
  customDateRange,
  presetPeriods = DEFAULT_PRESET_PERIODS,
  maxCustomDays,
  maxCustomDaysErrorMessage,
  onCustomRangeRejected,
}) => {
  const wrapperRef = useRef(null);
  const [rangeError, setRangeError] = useState("");
  const pickerOpen = usePickerOpenState(wrapperRef);
  const { dates, times, key: rangeKey } = splitCustomDateRange(customDateRange);

  const resolvedRangeErrorMessage = maxCustomDaysErrorMessage
    || (maxCustomDays ? `Custom date range cannot exceed ${maxCustomDays} days.` : "");

  const setRangeErrorStable = useCallback((message) => {
    setRangeError(message);
  }, []);

  usePickerRangeError(wrapperRef, rangeError, pickerOpen);
  useClearRangeErrorOnCalendarClick(wrapperRef, rangeError, setRangeErrorStable);

  useEffect(() => {
    if (timeRange !== "custom") setRangeError("");
  }, [timeRange]);

  const handleConfirm = (res) => {
    const normalized = normalizePickerDateRange(res);
    const invalid = validateCustomRangeSpan(normalized, maxCustomDays);
    if (invalid) {
      const message = resolvedRangeErrorMessage;
      setRangeError(message);
      onCustomRangeRejected?.(normalized, maxCustomDays);
      requestAnimationFrame(() => reopenPickerInstant(wrapperRef.current, message));
      return;
    }
    setRangeError("");
    paintPickerRangeError(wrapperRef.current, "");
    selectCustomDateRange(normalized);
  };

  const handleSelectPeriod = (period) => {
    setRangeError("");
    selectPeriod(period);
  };

  return (
    <Box ref={wrapperRef} sx={layerdRangePickerWrapperSx}>
      <RangePicker
        key={rangeKey}
        locale="en-us"
        show={false}
        disabled={false}
        allowPageClickToClose={true}
        onConfirm={handleConfirm}
        placeholder={["Start Time", "End Time"]}
        showOnlyTime={false}
        defaultDates={dates}
        defaultTimes={times}
        initialDates={dates}
        initialTimes={times}
      />
      <GreenSelectForm variant="outlined">
        <InputLabel id="time-range-selector">Time range</InputLabel>
        <Select
          labelId="time-range-selector"
          IconComponent={ArrowdownIcon}
          value={timeRange}
          MenuProps={timeRangeMenuProps}
          onChange={(e) => {
            if (e.target.value === "custom_value") {
              handleSelectPeriod("custom");
            } else {
              handleSelectPeriod(e.target.value);
            }
          }}
          sx={{ width: 400 }}
        >
          {presetPeriods.map((period) => (
            <MenuItem key={`period_${period}`} value={period} sx={timeRangeMenuItemSx}>
              {getPeriodString(period, customDateRange)}
            </MenuItem>
          ))}
          <MenuItem key="period_custom" value="custom_value" sx={timeRangeMenuItemSx}>
            Custom ...
          </MenuItem>
          <MenuItem key="period_custom_1" value="custom" sx={timeRangeMenuItemSx}>
            {getPeriodString("custom", customDateRange)}
          </MenuItem>
        </Select>
      </GreenSelectForm>
    </Box>
  );
};

/** Event listing pages — no 1M preset, max 7-day custom range, inline confirm validation. */
export function EventsTimeRangePicker(props) {
  return (
    <GreenTimeRangePicker
      presetPeriods={EVENT_LISTING_PRESET_PERIODS}
      maxCustomDays={EVENT_LISTING_MAX_CUSTOM_DAYS}
      {...props}
    />
  );
}
